import React, { ReactElement, ReactNode, useEffect } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import styled from "styled-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const StyledContainer = styled(Box)(({ theme }) => ({
    width: "inherit",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(4),
    padding: 0,
}));

const StyledTabs = styled(Tabs)(() => ({}));

export interface ITab {
    tabName: string;      // Should match route path (e.g., "personal", "career")
    headerText: string;
    content: ReactNode;
}

interface IProps {
    tabs: ITab[];
    basePath: string; // e.g., "/en"
}

function TabsContainer({ tabs, basePath }: IProps): ReactElement {
    const location = useLocation();
    const navigate = useNavigate();
    const { lang } = useParams(); // optional if you use language param

    const path = location.pathname.split("/").pop();
    const firstTabName = tabs[0].tabName;
    const activeTabName = tabs.some(t => t.tabName === path) ? path! : firstTabName;
    const activeTabIndex = tabs.findIndex(tab => tab.tabName === activeTabName);

    useEffect(() => {
        // If path is invalid, redirect to default tab
        if (activeTabIndex === -1) {
            navigate(`${basePath}/${firstTabName}`, { replace: true });
        }
    }, [activeTabIndex, navigate, basePath, firstTabName]);

    const handleTabChange = (_e: React.SyntheticEvent, newIndex: number) => {
        const newTab = tabs[newIndex].tabName;
        navigate(`${basePath}/${newTab}`);
    };

    return (
        <StyledContainer id="TabsContainer">
            <StyledTabs id="Tabs" value={activeTabIndex} onChange={handleTabChange}>
                {tabs.map(({ tabName, headerText }) => (
                    <Tab
                        id={`tab_${tabName}`}
                        key={`tab_${tabName}`}
                        label={headerText}
                        disabled={activeTabName === tabName}
                    />
                ))}
            </StyledTabs>
            {tabs[activeTabIndex]?.content}
        </StyledContainer>
    );
}

export default TabsContainer;
